import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const PAYMENT_FILTERS = [
  { key: "search", type: "search", label: "Search applicant, email, transaction id",
    paths: ["applicant_name", "applicant_email", "payment.transaction_id"] },
  { key: "payment_status", type: "select", label: "Payment status", path: "payment.status",
    options: [
      { value: "unpaid", label: "Unpaid" },
      { value: "pending_verification", label: "Pending verification" },
      { value: "verified", label: "Verified" },
      { value: "refunded", label: "Refunded" },
    ] },
  { key: "credential_type", type: "select", label: "Payment type", path: "credential_type",
    options: [
      { value: "BCT", label: "BCT" }, { value: "CHTC", label: "CHTC" },
      { value: "CTS", label: "CTS" }, { value: "LHT", label: "LHT" },
      { value: "institutional", label: "Institutional accreditation" },
      { value: "programmatic", label: "Programmatic accreditation" },
    ] },
  { key: "min_amount", type: "min", label: "Min amount", path: "payment.amount" },
  { key: "max_amount", type: "max", label: "Max amount", path: "payment.amount" },
  { key: "date_from", type: "dateFrom", label: "From", path: "payment.paid_at" },
  { key: "date_to", type: "dateTo", label: "To", path: "payment.paid_at" },
  { key: "is_deleted", type: "boolean", label: "Deleted", path: "is_deleted" },
];

export default function AdminPayments() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApps(data.filter((a) => a.payment?.status));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const verify = async (id, ok) => {
    try {
      await api.post(`/applications/${id}/payment/verify`, { verified: ok, note: "" });
      toast.success(ok ? "Payment verified" : "Payment rejected");
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const filters = useFilters(apps, PAYMENT_FILTERS, { defaultSort: { field: "payment.paid_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);

  return (
    <div className="animate-fade-up" data-testid="payments-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
        <h1 className="font-heading text-3xl font-bold text-primary">Payment verifications</h1>
        <p className="text-sm text-slate-500 mt-1">Confirm Square payment transaction IDs against application fees.</p>
      </div>

      <FilterPanel
        schemas={PAYMENT_FILTERS}
        values={filters.values}
        set={filters.set}
        clear={filters.clear}
        count={filters.count}
        total={apps.length}
        activeCount={filters.activeCount}
        sortFields={[
          { key: "payment.paid_at", label: "Payment date" },
          { key: "payment.amount", label: "Amount" },
          { key: "applicant_name", label: "Applicant name" },
        ]}
        sort={filters.sort}
        setSort={filters.setSort}
        testid="payments-filters"
      />

      {isAdmin && <BulkActionsBar collection="applications" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden divide-y divide-slate-100">
        {isAdmin && filters.filtered.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-slate-50 text-xs text-slate-600" data-testid="payments-select-all-row">
            <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="payments-select-all" />
            <span>Select all {filters.filtered.length}</span>
          </div>
        )}
        {loading && <div className="p-8 text-center text-slate-500">Loading…</div>}
        {!loading && filters.filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">No payments match the current filters</div>
        )}
        {filters.filtered.map((a) => (
          <PaymentRow
            key={a.id}
            app={a}
            onVerify={verify}
            isAdmin={isAdmin}
            selected={selection.isSelected(a.id)}
            onToggle={() => selection.toggle(a.id)}
          />
        ))}
      </Card>
    </div>
  );
}

function PaymentRow({ app, onVerify, isAdmin, selected, onToggle }) {
  const p = app.payment || {};
  const pending = p.status === "pending_verification";
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4 hover:bg-slate-50" data-testid={`payment-row-${app.id}`}>
      {isAdmin && (
        <Checkbox checked={selected} onCheckedChange={onToggle} data-testid={`payment-select-${app.id}`} />
      )}
      <div className="min-w-0">
        <div className="font-medium text-primary">{app.applicant_name}</div>
        <div className="text-xs text-slate-500">{app.applicant_email} · {app.credential_type}</div>
        {app.is_deleted && <span className="text-[9px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded mt-1 inline-block">Deleted</span>}
      </div>
      <div className="text-sm flex flex-wrap gap-x-6 gap-y-1">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Amount</div>
          <div className="font-heading font-bold text-primary">${(p.amount || 0).toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Status</div>
          <Badge variant="outline" className="capitalize">{p.status?.replace("_", " ")}</Badge>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Txn ID</div>
          <div className="font-mono-data text-xs text-primary">{p.transaction_id || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Submitted</div>
          <div className="text-xs text-slate-700">{p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}</div>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {pending && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onVerify(app.id, true)} data-testid={`verify-${app.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Verify
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => onVerify(app.id, false)} data-testid={`reject-${app.id}`}>
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
          </>
        )}
        <Link to={`/dashboard/applications/${app.id}`}>
          <Button size="sm" variant="ghost" className="text-primary">Open <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
        </Link>
      </div>
    </div>
  );
}
